import { TrendValue } from 'components/text';
import { AutoshipsColumn } from 'features/dashboard/widgets/firstline/columns/AutoshipsColumns';
import { CheckAssuranceColumn } from 'features/dashboard/widgets/firstline/columns/CheckAssuranceColumn';
import { FitlineClubMemberColumn } from 'features/dashboard/widgets/firstline/columns/FitlineClubMemberColumn';
import {
  CurrentQualificationColumn,
  HighesthQualificationColumn,
  PreviousMonthQualificationColumn
} from 'features/dashboard/widgets/firstline/columns/QualificationColumns';
import {
  OpenAutoshipVolumeColumn,
  PersonalSalesColumn,
  SixLevelSalesColumn,
  StructureSalesColumn
} from 'features/dashboard/widgets/firstline/columns/SalesColumns';
import { AutoshipsHeader } from 'features/dashboard/widgets/firstline/headers/AutoshipHeaders';
import { CheckAssuranceHeader } from 'features/dashboard/widgets/firstline/headers/CheckAssuranceHeader';
import { FitlineClubMemberHeader } from 'features/dashboard/widgets/firstline/headers/FitlineClubMemberHeader';
import {
  CurrentQualificationHeader,
  HighestQualificationHeader,
  PreviousQualificationHeader
} from 'features/dashboard/widgets/firstline/headers/QualificationHeaders';
import {
  OpenAutoshipVolumeHeader,
  PersonalSalesHeader,
  SixLevelSalesHeader,
  StructureSalesHeader
} from 'features/dashboard/widgets/firstline/headers/SalesHeaders';
import { IconName } from 'libs/icons';
import _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import { computePercentageTrend } from 'utils';

import { ActionsCellRenderer } from '../renderers/ActionsCellRenderer';
import { UserCellRenderer } from '../renderers/UserCellRenderer';
import { IColumnConfig } from '../types';

export enum UserTableColumns {
  Actions = 'actions',
  User = 'user',
  CheckAssurance = 'checkAssurance',
  CurrentQualification = 'currentQualification',
  PreviousQualification = 'previousQualification',
  HighestQualification = 'highestQualification',
  FitlineClubMember = 'fitlineClubMember',
  Autoships = 'autoships',
  PersonalSales = 'personalSales',
  OpenAutoshipVolume = 'openAutoshipVolume',
  SixLevelSales = 'sixLevelSales',
  SixLevelTrend = 'sixLevelTrend',
  StructureSales = 'structureSales',
  StructureTrend = 'structureTrend'
}

export const defaultUserColumns: IColumnConfig[] = [
  {
    key: UserTableColumns.Actions,
    header: '',
    render: user => <ActionsCellRenderer user={user} />,
    cellClassName: user => (user.structureType === 'downline' ? 'bg-body' : '')
  },
  {
    key: UserTableColumns.User,
    header: '',
    render: (user, context) => (
      <UserCellRenderer
        user={user}
        onUserClick={() => context?.setIsExpanded(prev => !prev)}
        iconName={
          !context?.isExpanded ? IconName.ChevronDown : IconName.ChevronUp
        }
      />
    ),
    className: 'px-2',
    cellClassName: (user, context) =>
      user.structureType === 'upline' && !context?.isExpanded
        ? 'relative after:w-[2px] after:h-2 after:bg-silver after:absolute after:bottom-[-5px] after:left-[22px]'
        : ''
  },
  {
    key: UserTableColumns.CheckAssurance,
    header: <CheckAssuranceHeader iconOnly />,
    render: user => <CheckAssuranceColumn userProfile={user} />,
    align: 'center',
    className: 'px-2'
  },
  {
    key: UserTableColumns.CurrentQualification,
    header: <CurrentQualificationHeader iconOnly />,
    render: user => <CurrentQualificationColumn userProfile={user} />,
    align: 'center',
    className: 'px-2'
  },
  {
    key: UserTableColumns.PreviousQualification,
    header: <PreviousQualificationHeader iconOnly />,
    render: user => <PreviousMonthQualificationColumn userProfile={user} />,
    align: 'center',
    className: 'px-2'
  },
  {
    key: UserTableColumns.HighestQualification,
    header: <HighestQualificationHeader iconOnly />,
    render: user => <HighesthQualificationColumn userProfile={user} />,
    align: 'center',
    className: 'px-2'
  },
  {
    key: UserTableColumns.FitlineClubMember,
    header: <FitlineClubMemberHeader iconOnly />,
    render: user => <FitlineClubMemberColumn userProfile={user} />,
    align: 'center',
    className: 'px-2'
  },
  {
    key: UserTableColumns.Autoships,
    header: <AutoshipsHeader />,
    render: user => <AutoshipsColumn userProfile={user} />,
    align: 'center',
    className: 'px-4'
  },
  {
    key: UserTableColumns.PersonalSales,
    header: <PersonalSalesHeader />,
    render: user => <PersonalSalesColumn userProfile={user} />,
    align: 'right',
    className: 'px-2'
  },
  {
    key: UserTableColumns.OpenAutoshipVolume,
    header: <OpenAutoshipVolumeHeader />,
    render: user => <OpenAutoshipVolumeColumn userProfile={user} />,
    align: 'right',
    className: 'px-4'
  },
  {
    key: UserTableColumns.SixLevelSales,
    header: <SixLevelSalesHeader />,
    render: user => <SixLevelSalesColumn userProfile={user} />,
    align: 'right',
    className: 'pl-4',
    colSpan: 2
  },
  {
    key: UserTableColumns.SixLevelTrend,
    header: '',
    render: user => {
      if (_.isUndefined(user.businessData)) return null;
      const trend = computePercentageTrend(
        user.businessData.turnoverFromSixLevel.current,
        user.businessData.turnoverFromSixLevel.lastMonth
      );
      return !_.isUndefined(trend) ? (
        <TrendValue value={trend} style="illustrated" />
      ) : null;
    },
    align: 'left',
    className: 'pr-4 pl-2'
  },
  {
    key: UserTableColumns.StructureSales,
    header: <StructureSalesHeader />,
    render: user => <StructureSalesColumn userProfile={user} />,
    align: 'right',
    className: 'pl-4',
    colSpan: 2
  },
  {
    key: UserTableColumns.StructureTrend,
    header: '',
    render: user => {
      if (_.isUndefined(user.businessData)) return null;
      const trend = computePercentageTrend(
        user.businessData.turnoverFromStructure.current,
        user.businessData.turnoverFromStructure.lastMonth
      );
      return !_.isUndefined(trend) ? (
        <TrendValue value={trend} style="illustrated" />
      ) : null;
    },
    align: 'left',
    className: 'pr-4 pl-2'
  }
];

export function getUserColumnsByKeys(
  userTableColumns: UserTableColumns[]
): IColumnConfig[] {
  const columns: IColumnConfig[] = [];

  defaultUserColumns.forEach(column => {
    if (userTableColumns.includes(column.key)) {
      columns.push(column);
    }
  });

  return columns;
}
